import { Link } from 'react-router-dom';
import {
  AcademicCapIcon,
  ArrowRightIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  IdentificationIcon,
  Squares2X2Icon,
  StarIcon,
  UserGroupIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import AdminHeader from '../../../shared/components/AdminHeader';
import { useAuth } from '../../../core/providers/AuthProvider';
import { ROLES } from '../../../shared/constant/roles';
import { getGradeLevelLabel } from '../../../shared/constant/gradeLevels';
import { useAttentionList } from '../hooks/useAttentionList';
import { useTeacherKpis } from '../hooks/useTeacherKpis';
import AttentionSection from './AttentionSection';
import AttendanceTrendChart from './AttendanceTrendChart';

const CARDS = [
  {
    label: 'Teachers',
    description: 'Add, edit, and remove teacher records.',
    to: '/dashboard/teachers',
    icon: UsersIcon,
    hiddenForRoles: [ROLES.TEACHER],
  },
  {
    label: 'Students',
    description: 'Add, edit, and remove student records.',
    to: '/dashboard/students',
    icon: IdentificationIcon,
  },
  {
    label: 'Subjects',
    description: 'View subjects and their assigned teachers.',
    to: '/dashboard/subjects',
    icon: BookOpenIcon,
    hiddenForRoles: [ROLES.TEACHER],
  },
  {
    label: 'Grades Per Subject',
    description: 'View and manage grades.',
    to: '/dashboard/grades',
    icon: AcademicCapIcon,
    onlyForRoles: [ROLES.TEACHER],
  },
  {
    label: 'Advisory',
    description: 'View the grades and attendance of your advisory class.',
    to: '/dashboard/advisory',
    icon: UserGroupIcon,
    onlyForRoles: [ROLES.TEACHER],
  },
  {
    label: 'Attendance Summary',
    description: 'View attendance by section, day, or month.',
    to: '/dashboard/attendance-summary',
    icon: CalendarDaysIcon,
    onlyForRoles: [ROLES.TEACHER, ROLES.ADMIN, ROLES.SUPER_ADMIN],
  },
  {
    label: 'Secretary Assignment',
    description: 'Assign a class secretary for any section.',
    to: '/dashboard/secretary-assignment',
    icon: StarIcon,
    onlyForRoles: [ROLES.ADMIN, ROLES.SUPER_ADMIN],
  },
];

// One headline number for a teacher. `value` is null while loading or when it couldn't be loaded.
function KpiCard({ icon: Icon, label, value, hint, loading }) {
  return (
    <div className="min-w-0 flex items-center gap-3 bg-white rounded-lg p-3 sm:p-4">
      <div className="h-10 w-10 shrink-0 rounded-md bg-[#0b4a71]/10 flex items-center justify-center">
        <Icon className="h-6 w-6 text-[#0b4a71]" />
      </div>
      <div className="min-w-0">
        <p className="truncate text-xs sm:text-sm text-gray-500">{label}</p>
        {loading ? (
          <div className="mt-1 h-8 w-12 rounded bg-gray-100 animate-pulse" />
        ) : (
          <p className="text-2xl sm:text-3xl font-bold leading-tight text-[#0b4a71]">{value ?? '—'}</p>
        )}
        {hint && !loading && <p className="truncate text-xs text-gray-500">{hint}</p>}
      </div>
    </div>
  );
}

function DashboardPage() {
  const { role, teacherName, teacherNameLoading } = useAuth();
  const isTeacherRole = role === ROLES.TEACHER;
  const { kpis, loading: kpisLoading } = useTeacherKpis(isTeacherRole);
  const attentionList = useAttentionList(isTeacherRole);
  const cards = CARDS.filter(
    (card) => !card.hiddenForRoles?.includes(role) && (!card.onlyForRoles || card.onlyForRoles.includes(role))
  );

  return (
    <div className="h-dvh w-full flex flex-col bg-appbg overflow-hidden">
      <AdminHeader />

      <div className="flex-1 min-h-0 overflow-y-auto">
        <main className="w-full p-4 lg:w-3/4 lg:mx-auto space-y-4">
          <div>
            {teacherNameLoading ? (
              <div className="h-7 w-40 rounded animate-pulse mb-1" />
            ) : (
              teacherName && <p className="text-lg text-gray-700">Welcome, {teacherName.toUpperCase()}</p>
            )}
            <h1 className="text-2xl font-semibold text-[#0b4a71]">Dashboard</h1>
          </div>

          {isTeacherRole && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <KpiCard icon={BookOpenIcon} label="Subjects" value={kpis?.subjects} loading={kpisLoading} />
              <KpiCard icon={Squares2X2Icon} label="Sections Handled" value={kpis?.sections} loading={kpisLoading} />
              <KpiCard icon={IdentificationIcon} label="Students" value={kpis?.students} loading={kpisLoading} />
              <KpiCard
                icon={UserGroupIcon}
                label="Advisory"
                value={kpis?.advisory ? kpis.advisory.students : null}
                hint={kpis?.advisory ? `${getGradeLevelLabel(kpis.advisory.grade_level)} - ${kpis.advisory.section}` : 'No advisory class'}
                loading={kpisLoading}
              />
            </div>
          )}

          {isTeacherRole && <AttentionSection {...attentionList} />}

          {isTeacherRole && <AttendanceTrendChart />}

          <h2 className="text-lg font-semibold text-[#0b4a71]">Quick Links</h2>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {cards.map(({ label, description, to, icon: Icon }) => (
              <Link
                key={label}
                to={to}
                className="group relative flex flex-col sm:flex-row sm:items-start gap-3 bg-white rounded-lg border border-transparent p-5 hover:border-[#4ea6d0] hover:shadow-md transition"
              >
                <div className="h-10 w-10 shrink-0 rounded-md bg-[#0b4a71]/10 flex items-center justify-center">
                  <Icon className="h-6 w-6 text-[#0b4a71]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-[#0b4a71]">{label}</p>
                  <p className="text-sm text-gray-500">{description}</p>
                </div>
                {/* Pinned to the top right corner on phones (icon-on-top card); in the row from sm up. */}
                <ArrowRightIcon
                  className="absolute top-4 right-4 sm:static h-5 w-5 shrink-0 text-gray-400 transition group-hover:translate-x-0.5 group-hover:text-[#0b4a71]"
                  aria-hidden="true"
                />
              </Link>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
}

export default DashboardPage;
